// js/sections/geo-location-popup.js
import { Drawer as Drawer3 } from "//www.liewood.com/cdn/shop/t/284/assets/custom.js?v=179797621696423827261758529405";

class GeoLocationPopup extends Drawer3 {
  constructor() {
    super();
    this.state = {
      currentView: 'initial',
      isOnlyOnce: this.hasAttribute('only-once')
    };
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Check if user arrived via geo selection link
    this.checkGeoSelectedParam();
    
    // Cache DOM elements when component is connected to DOM
    this.initialView = this.querySelector('[data-initial-view]');
    this.changeView = this.querySelector('[data-change-view]');
    this.nextStepButton = this.querySelector('[data-next-step-button]');
    this.changeLink = this.querySelector('[data-change-link]');
    this.cancelButton = this.querySelector('[data-cancel-button]');
    // NOTE: Form-based localization is hidden in HTML, hardcoded country links are used instead
    this.form = this.querySelector('#GeoLocationForm');
    this.countrySelect = this.querySelector('[data-country-selector]');
    this.languageSelect = this.querySelector('[data-language-selector]');
    
    // Setup event listeners
    this.setupEventListeners();

    if (this.shouldAppearAutomatically) {
      this.checkGeolocation();
    }
  }

  get initialFocus() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }

  get shouldAppearAutomatically() {
    const storageState = this.checkStorage();
    return !(
      storageState.filled ||
      (this.state.isOnlyOnce && storageState.appeared)
    );
  }

  setupEventListeners() {
    // Initial view - continue button
    this.nextStepButton?.addEventListener('click', () => {
      this.saveToStorage();
      this.close();
    });

    // Change country/language link
    this.changeLink?.addEventListener('click', () => {
      this.showChangeView();
    });

    // Cancel button in form view
    this.cancelButton?.addEventListener('click', () => {
      this.showInitialView();
    });

    // Close button (using is="close-button" attribute)
    this.querySelector('[is="close-button"]')?.addEventListener('click', () => {
      this.saveToStorage();
      this.close();
    });

    // Click outside to close or cancel button
    this.addEventListener('click', (event) => {
      if (event.target === this || event.target.classList.contains("btn-geo-close-selector")) {
        this.saveToStorage();
        this.close();
      }
    });

    // Country link clicks - save to storage before navigating
    this.querySelectorAll('.geo-location-CountriesList__item').forEach(link => {
      link.addEventListener('click', () => {
        this.saveToStorage();
      });
    });

    // Manual trigger buttons (e.g., in header/footer to manually open popup)
    this.setupManualTriggers();

    // NOTE: Form submission handler - currently not used as form is hidden in HTML
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }

  setupManualTriggers() {
    document.querySelectorAll('.btn-geo-location-trigger').forEach(trigger => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.show();
      });
    });
  }

  // Helper method to save to appropriate storage
  saveToStorage() {
    const storage = this.state.isOnlyOnce ? localStorage : sessionStorage;
    storage.setItem('theme:geo-location-filled', 'true');
    storage.setItem('theme:geo-location-appeared', 'true');
  }

  // Helper method to check storage
  checkStorage() {
    const storage = this.state.isOnlyOnce ? localStorage : sessionStorage;
    return {
      filled: storage.getItem('theme:geo-location-filled') === 'true',
      appeared: storage.getItem('theme:geo-location-appeared') === 'true'
    };
  }

  // Check if user arrived via geo selection link and save choice
  checkGeoSelectedParam() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('geo_selected') === 'true') {
      // User made an active choice, save to storage
      this.saveToStorage();
      
      // Remove the parameter from URL without reloading
      if (window.history.replaceState) {
        urlParams.delete('geo_selected');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }

  // Get expected country code for current hostname
  getExpectedCountry() {
    const hostname = window.location.hostname;
    
    // Handle both www and non-www versions
    if (hostname.includes('liewood.de')) return 'DE';
    if (hostname.includes('liewood.fr')) return 'FR';
    if (hostname.includes('liewood.com')) return 'US';
    if (hostname.includes('liewood.dk')) return 'DK';
    if (hostname.includes('myshopify.com')) return null; // Test store, always show
    
    return undefined; // Unknown hostname, don't show popup
  }

  // Check user's geolocation and show popup if they're on the wrong store
  async checkGeolocation() {
    const expectedCountry = this.getExpectedCountry();
    
    // If hostname not recognized, don't show popup
    if (expectedCountry === undefined) {
      return;
    }

    // For test store, always show after delay
    if (expectedCountry === null) {
      setTimeout(() => this.show(), this.apparitionDelay);
      return;
    }

    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error('Geolocation API request failed');
      }
      
      const data = await response.json();
      const userCountryCode = data.country_code;

      // Show popup if user's country doesn't match the store's country
      if (userCountryCode && userCountryCode !== expectedCountry) {
        setTimeout(() => this.show(), this.apparitionDelay);
      }
    } catch (error) {
      console.error('Error fetching geolocation:', error);
      // Don't show popup on error to avoid annoying users
    }
  }

  // NOTE: Form submission handler - currently not used as form is hidden in HTML
  // If form is enabled in future, this will handle the Shopify localization form submission
  handleFormSubmit(event) {
    event.preventDefault();

    const submitButton = this.form.querySelector('[data-submit-button]');
    if (submitButton) {
      submitButton.disabled = true;
    }

    try {
      // Save to appropriate storage before submitting
      this.saveToStorage();

      // Use native form submission
      this.form.submit();
    } catch (error) {
      console.error('Form submission error:', error);
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  }

  close() {
    // Save to storage before closing
    this.saveToStorage();
    // Call parent hide() to remove open attribute and handle animations
    super.hide();
  }

  showInitialView() {
    this.state.currentView = 'initial';
    this.initialView.hidden = false;
    this.changeView.hidden = true;
  }

  showChangeView() {
    this.state.currentView = 'change';
    this.initialView.hidden = true;
    this.changeView.hidden = false;
  }

  _setInitialPosition() {
    this.style.top = null;
    this.style.bottom = "0px";
    this.style.left = document.dir === "ltr" ? null : "0px";
    this.style.right = document.dir === "rtl" ? "auto" : "0px";
  }
}

if (!window.customElements.get('geo-location-popup')) {
  window.customElements.define('geo-location-popup', GeoLocationPopup);
}
